import { AlertColor } from "@mui/material";
import { SnackbarOrigin } from "@mui/material/Snackbar";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { CapturedError, captureError, ErrorContext } from "@/utils/clientErrorTracking";

export interface SnackbarMessage {
  id: string;
  message: string;
  severity: AlertColor;
  autoHideDuration?: number | null;
  anchorOrigin?: SnackbarOrigin;
  onClick?: () => void;
  capturedError?: CapturedError;
  errorContext?: ErrorContext;
}

interface SnackbarOptions {
  autoHideDuration?: number | null;
  anchorOrigin?: SnackbarOrigin;
  onClick?: () => void;
  capturedError?: CapturedError;
  errorContext?: ErrorContext;
}

interface SnackbarState {
  queue: SnackbarMessage[];
  current: SnackbarMessage | null;
}

interface SnackbarActions {
  enqueue: (message: string, severity: AlertColor, options?: Partial<SnackbarOptions>) => string;
  /**
   * Shows an error message with severity based on error type:
   * - Uses "error" severity (red) when `capturedError` is provided (persistent, unexpected errors)
   * - Uses "warning" severity (orange) otherwise (validation errors, user-fixable issues)
   *
   * This distinction helps users differentiate between system errors (red) that need
   * support intervention and validation/business errors (orange) they can resolve.
   */
  showError: (message: string, options?: Partial<SnackbarOptions>) => string;
  /**
   * Captures an error to Sentry/PostHog and shows a persistent error snackbar.
   * Combines captureError + showError into a single call for cleaner catch blocks.
   */
  showCapturedError: (error: unknown, context: ErrorContext, eventName?: string) => string;
  showSuccess: (message: string, options?: Partial<SnackbarOptions>) => string;
  showWarning: (message: string, options?: Partial<SnackbarOptions>) => string;
  showInfo: (message: string, options?: Partial<SnackbarOptions>) => string;
  dismiss: () => void;
  processQueue: () => void;
  clearQueue: () => void;
  removeMessages: (messageIds: string[]) => void;
}

export type SnackbarStore = SnackbarState & SnackbarActions;

const initialState: SnackbarState = {
  queue: [],
  current: null,
};

const DEFAULT_AUTO_HIDE_DURATION = 6000;
const DEFAULT_ANCHOR_ORIGIN: SnackbarOrigin = { vertical: "bottom", horizontal: "center" };
const PROCESS_QUEUE_DELAY_MS = 100;

const queueTimerManager = {
  timerId: null as ReturnType<typeof setTimeout> | null,

  schedule(callback: () => void): void {
    this.cancel();
    this.timerId = setTimeout(() => {
      this.timerId = null;
      callback();
    }, PROCESS_QUEUE_DELAY_MS);
  },

  cancel(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  },
};

const getMessageCategory = (message: string): string => {
  if (/insufficient\s+points/i.test(message)) {
    return "insufficient-points";
  }
  return message;
};

export const useSnackbarStore = create<SnackbarStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      enqueue: (message, severity, options = {}) => {
        const { current, queue } = get();
        const category = getMessageCategory(message);

        const existingCurrent =
          current && current.severity === severity && getMessageCategory(current.message) === category;
        const existingInQueue = queue.find(
          (item) => item.severity === severity && getMessageCategory(item.message) === category,
        );

        if (existingCurrent || existingInQueue) {
          return existingCurrent ? current.id : existingInQueue?.id || "";
        }

        const id = `snackbar-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const defaultDuration = options.capturedError ? null : DEFAULT_AUTO_HIDE_DURATION;
        const autoHideDuration = "autoHideDuration" in options ? options.autoHideDuration : defaultDuration;
        const snackbarMessage: SnackbarMessage = {
          id,
          message,
          severity,
          autoHideDuration,
          anchorOrigin: options.anchorOrigin ?? DEFAULT_ANCHOR_ORIGIN,
          onClick: options.onClick,
          capturedError: options.capturedError,
          errorContext: options.errorContext,
        };

        set(
          (state) => ({
            queue: [...state.queue, snackbarMessage],
          }),
          undefined,
          "snackbar/enqueue",
        );

        get().processQueue();
        return id;
      },

      showError: (message, options) => {
        const severity = options?.capturedError ? "error" : "warning";
        return get().enqueue(message, severity, options);
      },

      showCapturedError: (error, context, eventName = "client_error") => {
        const captured = captureError(error, context, eventName);
        return get().showError(captured.userMessage, {
          capturedError: captured,
          errorContext: context,
        });
      },

      showSuccess: (message, options) => get().enqueue(message, "success", options),

      showWarning: (message, options) => get().enqueue(message, "warning", options),

      showInfo: (message, options) => get().enqueue(message, "info", options),

      dismiss: () => {
        set(
          {
            current: null,
          },
          undefined,
          "snackbar/dismiss",
        );

        queueTimerManager.schedule(() => get().processQueue());
      },

      processQueue: () => {
        const { current, queue } = get();

        if (!current && queue.length > 0) {
          const [next, ...rest] = queue;
          set(
            {
              current: next,
              queue: rest,
            },
            undefined,
            "snackbar/processQueue",
          );
        }
      },

      clearQueue: () => {
        queueTimerManager.cancel();

        set(
          {
            queue: [],
            current: null,
          },
          undefined,
          "snackbar/clearQueue",
        );
      },

      removeMessages: (messageIds) => {
        const { current, queue } = get();
        const idsToRemove = new Set(messageIds);

        if (current && idsToRemove.has(current.id)) {
          set(
            {
              current: null,
            },
            undefined,
            "snackbar/removeMessages - clear current",
          );

          queueTimerManager.schedule(() => get().processQueue());
        }

        const filteredQueue = queue.filter((item) => !idsToRemove.has(item.id));
        if (filteredQueue.length !== queue.length) {
          set(
            {
              queue: filteredQueue,
            },
            undefined,
            "snackbar/removeMessages - filter queue",
          );
        }
      },
    }),
    {
      name: "snackbar-store",
    },
  ),
);
